import type { Lang } from '../page';

interface ProductOverviewProps { lang: Lang; }

type LangMap = Record<Lang, string>;

const sectionTag: LangMap = {
  en: 'CORE FEATURES', zh: '核心特性', th: 'คุณสมบัติหลัก',
  vi: 'TÍNH NĂNG CỐT LÕI', ms: 'CIRI-CIRI UTAMA',
};
const sectionTitle: LangMap = {
  en: 'Six Key Advantages', zh: '六大核心优势', th: 'หกข้อได้เปรียบหลัก',
  vi: 'Sáu Lợi Thế Chính', ms: 'Enam Kelebihan Utama',
};
const sectionSub: LangMap = {
  en: 'Leading Industry Standards', zh: '引领行业标准', th: 'นำหน้ามาตรฐานอุตสาหกรรม',
  vi: 'Dẫn Đầu Tiêu Chuẩn Ngành', ms: 'Memimpin Piawaian Industri',
};

const features: Array<{ icon: string; title: LangMap; sub: LangMap; desc: LangMap }> = [
  {
    icon: 'ri-fire-line',
    title: { en: 'Ultra High Temperature Resistance', zh: '超高耐温性能', th: 'ทนอุณหภูมิสูงพิเศษ', vi: 'Chịu Nhiệt Độ Siêu Cao', ms: 'Rintangan Suhu Ultra Tinggi' },
    sub:   { en: '超高耐温性能', zh: 'Ultra High Temp', th: 'Ultra High Temperature', vi: 'Khả năng chịu nhiệt', ms: 'Rintangan Suhu Tinggi' },
    desc:  {
      en: 'Continuous operating temperature up to 260°C, short-term resistance to 400°C. Maintains stable physical and chemical properties in extreme high-temperature environments.',
      zh: '连续使用温度高达260°C，短期耐温可达400°C。在极端高温环境下保持稳定的物理化学性能。',
      th: 'อุณหภูมิใช้งานต่อเนื่องสูงถึง 260°C ทนระยะสั้นได้ถึง 400°C คงคุณสมบัติทางกายภาพและเคมีในสภาวะอุณหภูมิสูงสุดขีด',
      vi: 'Nhiệt độ hoạt động liên tục lên đến 260°C, chịu nhiệt ngắn hạn đến 400°C. Duy trì tính chất ổn định trong môi trường nhiệt độ cực cao.',
      ms: 'Suhu operasi berterusan sehingga 260°C, rintangan jangka pendek sehingga 400°C. Mengekalkan sifat fizikal dan kimia yang stabil dalam persekitaran suhu tinggi.',
    },
  },
  {
    icon: 'ri-flashlight-line',
    title: { en: 'Excellent Electrical Insulation', zh: '优异电气绝缘', th: 'ฉนวนไฟฟ้าดีเยี่ยม', vi: 'Cách Điện Tuyệt Vời', ms: 'Penebat Elektrik Cemerlang' },
    sub:   { en: '优异电气绝缘', zh: 'Excellent Insulation', th: 'Excellent Electrical Insulation', vi: 'Cách điện xuất sắc', ms: 'Penebat Elektrik' },
    desc:  {
      en: 'Dielectric strength up to 200kV/mm, volume resistivity greater than 10¹⁷Ω·cm. Provides superior electrical insulation for motors, transformers and electrical equipment.',
      zh: '介电强度高达200kV/mm，体积电阻率大于10¹⁷Ω·cm。为电机、变压器及电气设备提供卓越的电气绝缘性能。',
      th: 'ความแข็งแรงทางไฟฟ้าสูงถึง 200kV/mm ความต้านทานเชิงปริมาตรมากกว่า 10¹⁷Ω·cm เหมาะสำหรับมอเตอร์และหม้อแปลงไฟฟ้า',
      vi: 'Độ bền điện môi lên đến 200kV/mm, điện trở suất thể tích lớn hơn 10¹⁷Ω·cm. Cách điện vượt trội cho động cơ và máy biến áp.',
      ms: 'Kekuatan dielektrik sehingga 200kV/mm, kerintangan isipadu melebihi 10¹⁷Ω·cm. Penebatan elektrik unggul untuk motor dan transformer.',
    },
  },
  {
    icon: 'ri-shield-check-line',
    title: { en: 'Outstanding Mechanical Strength', zh: '卓越机械强度', th: 'ความแข็งแรงทางกลที่ยอดเยี่ยม', vi: 'Độ Bền Cơ Học Vượt Trội', ms: 'Kekuatan Mekanikal Luar Biasa' },
    sub:   { en: '卓越机械强度', zh: 'Outstanding Mechanical', th: 'Outstanding Mechanical Strength', vi: 'Độ bền cơ học', ms: 'Kekuatan Mekanikal' },
    desc:  {
      en: 'Tensile strength exceeds 170MPa, elongation at break over 70%. Excellent tear and puncture resistance ensures reliability in complex working conditions.',
      zh: '抗拉强度超过170MPa，断裂伸长率超过70%。优异的抗撕裂和抗穿刺性能确保复杂工况下的可靠性。',
      th: 'ความแข็งแรงดึงมากกว่า 170MPa การยืดตัวก่อนขาดมากกว่า 70% ทนการฉีกขาดและการเจาะทะลุได้ดีเยี่ยม',
      vi: 'Độ bền kéo vượt 170MPa, độ giãn dài khi đứt trên 70%. Chống rách và chống đâm thủng xuất sắc.',
      ms: 'Kekuatan tegangan melebihi 170MPa, pemanjangan semasa patah melebihi 70%. Rintangan koyak dan cucukan yang cemerlang.',
    },
  },
  {
    icon: 'ri-test-tube-line',
    title: { en: 'Superior Chemical Stability', zh: '化学稳定性强', th: 'เสถียรภาพทางเคมีที่เหนือกว่า', vi: 'Độ Bền Hóa Học Vượt Trội', ms: 'Kestabilan Kimia Unggul' },
    sub:   { en: '化学稳定性强', zh: 'Chemical Stability', th: 'Superior Chemical Stability', vi: 'Độ bền hóa học', ms: 'Kestabilan Kimia' },
    desc:  {
      en: 'Resistant to acids, alkalis, organic solvents and radiation. Maintains stability in harsh chemical environments, suitable for chemical and nuclear industry applications.',
      zh: '耐酸碱、有机溶剂和辐射。在恶劣化学环境中保持稳定性，适用于化工及核工业应用。',
      th: 'ทนกรด-ด่าง ตัวทำละลายอินทรีย์ และรังสี คงความเสถียรในสภาพแวดล้อมทางเคมีที่รุนแรง',
      vi: 'Chống axit, kiềm, dung môi hữu cơ và bức xạ. Duy trì độ ổn định trong môi trường hóa học khắc nghiệt.',
      ms: 'Tahan asid, alkali, pelarut organik dan sinaran. Mengekalkan kestabilan dalam persekitaran kimia yang keras.',
    },
  },
  {
    icon: 'ri-ruler-line',
    title: { en: 'High Dimensional Precision', zh: '尺寸精度高', th: 'ความแม่นยำขนาดสูง', vi: 'Độ Chính Xác Kích Thước Cao', ms: 'Ketepatan Dimensi Tinggi' },
    sub:   { en: '尺寸精度高', zh: 'High Precision', th: 'High Dimensional Precision', vi: 'Độ chính xác cao', ms: 'Ketepatan Tinggi' },
    desc:  {
      en: 'Thickness tolerance controlled within ±5%, excellent surface flatness. Ensures product consistency and processing precision for precision manufacturing.',
      zh: '厚度公差控制在±5%以内，表面平整度优异。确保产品一致性和加工精度，满足精密制造的严格要求。',
      th: 'ควบคุมความคลาดเคลื่อนของความหนาภายใน ±5% ความเรียบผิวดีเยี่ยม รับประกันความสม่ำเสมอของผลิตภัณฑ์',
      vi: 'Dung sai độ dày trong ±5%, độ phẳng bề mặt xuất sắc. Đảm bảo tính nhất quán và độ chính xác gia công.',
      ms: 'Toleransi ketebalan dikawal dalam ±5%, kerataan permukaan cemerlang. Memastikan konsistensi produk untuk pembuatan presisi.',
    },
  },
  {
    icon: 'ri-leaf-line',
    title: { en: 'Eco-Friendly & Recyclable', zh: '环保可回收', th: 'เป็นมิตรกับสิ่งแวดล้อม', vi: 'Thân Thiện Môi Trường', ms: 'Mesra Alam & Boleh Dikitar Semula' },
    sub:   { en: '环保可回收', zh: 'Eco-Friendly', th: 'Eco-Friendly & Recyclable', vi: 'Thân thiện môi trường', ms: 'Mesra Alam' },
    desc:  {
      en: 'Complies with RoHS and REACH environmental standards. Materials are recyclable, green production process helps enterprises achieve sustainable development goals.',
      zh: '符合RoHS和REACH环保标准。材料可回收再利用，绿色生产工艺助力企业实现可持续发展目标。',
      th: 'เป็นไปตามมาตรฐาน RoHS และ REACH วัสดุสามารถรีไซเคิลได้ กระบวนการผลิตสีเขียวช่วยให้บรรลุเป้าหมายการพัฒนาอย่างยั่งยืน',
      vi: 'Tuân thủ tiêu chuẩn môi trường RoHS và REACH. Vật liệu có thể tái chế, quy trình sản xuất xanh hỗ trợ phát triển bền vững.',
      ms: 'Mematuhi piawaian alam sekitar RoHS dan REACH. Bahan boleh dikitar semula, proses pengeluaran hijau membantu mencapai matlamat pembangunan lestari.',
    },
  },
];

export default function ProductOverview({ lang }: ProductOverviewProps) {
  return (
    <section id="features" className="py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="mb-16">
          <div className="text-sm font-medium text-gray-500 tracking-wider mb-4">{sectionTag[lang]}</div>
          <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-4">{sectionTitle[lang]}</h2>
          <p className="text-3xl lg:text-4xl font-bold text-amber-500">{sectionSub[lang]}</p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div
              key={index}
              className="group bg-white rounded-3xl p-10 shadow-sm hover:shadow-xl hover:-translate-y-2 transition-all duration-300 border border-gray-100"
            >
              <div className="w-16 h-16 flex items-center justify-center mb-6">
                <i className={`${feature.icon} text-5xl text-amber-500`}></i>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">{feature.title[lang]}</h3>
              <h4 className="text-lg font-semibold text-gray-600 mb-4">{feature.sub[lang]}</h4>
              <p className="text-gray-600 leading-relaxed">{feature.desc[lang]}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
